import { MEDIA_LIBRARY_API_URL } from 'constants/ApiConstants';
import * as actionTypes from 'modules/mediaLibrary/actionTypes';
import { DEFAULT_PAGE_SIZE } from 'modules/mediaLibrary/constants';

/**
 * @param {import('./reducer/ui/filters').FilterField} field
 * @param {unknown} value
 * @param {import('./types').Meta} meta
 * @return {import('./types').SetMediaLibraryFilterAction}
 */
export const setFilters = (field, value, meta = {}) => ({
  type: actionTypes.SET_MEDIA_LIBRARY_FILTER,
  payload: { field, value },
  meta,
});

/**
 * @param {import('./reducer/ui/filters').FilterField} field
 * @param {unknown} value
 * @param {import('./types').Meta} meta
 * @return {import('./types').RemoveMediaLibraryFilterAction}
 */
export const removeFilter = (field, value, meta = {}) => ({
  type: actionTypes.REMOVE_MEDIA_LIBRARY_FILTER,
  payload: { field, value },
  meta,
});

/**
 * @param {import('./types').Meta} meta
 * @return {import('./types').ClearMediaLibraryFiltersAction}
 */
export const clearFilters = (meta = {}) => ({
  type: actionTypes.CLEAR_MEDIA_LIBRARY_FILTERS,
  meta,
});

/**
 * @param {import("./types").FetchMediaLibraryPayload} params
 * @param {import("./types").Meta} meta
 * @return {import("./types").FetchMediaLibraryAction}
 */
export const fetchMediaLibrary = (params, meta = {}) => ({
  type: actionTypes.FETCH_MEDIA_LIBRARY,
  payload: {
    request: {
      method: 'GET',
      url: MEDIA_LIBRARY_API_URL,
      params: {
        projection: 'page',
        paginationType: 'SLICE',
        ...params,
        size: DEFAULT_PAGE_SIZE,
      },
    },
  },
  meta,
});

/**
 * @param {import('./types').Media[]} data
 * @param {import('./types').Meta} meta
 * @returns {import('./types').FetchMediaLibrarySuccessAction}
 */
export const fetchMediaLibrarySuccess = (data, meta = {}) => ({
  type: actionTypes.FETCH_MEDIA_LIBRARY_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {import('./types').Meta} meta
 * @returns {import('./types').FetchMediaLibraryFailureAction}
 */
export const fetchMediaLibraryFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_MEDIA_LIBRARY_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} mediaLibraryId
 * @param {import('./types').UpdateMediaPayload} data
 * @param {import('./types').Meta} meta
 * @return {import('./types').UpdateMediaAction}
 */
export const updateMediaLibrary = (mediaLibraryId, data, meta = {}) => ({
  type: actionTypes.UPDATE_MEDIA_LIBRARY,
  payload: {
    request: {
      method: 'PUT',
      url: `${MEDIA_LIBRARY_API_URL}/${mediaLibraryId}`,
      data,
    },
  },
  meta,
});

/**
 * @param {import('./types').Media} content
 * @param {import('./types').Meta} meta

 * @returns {import('./types').UpdateMediaSuccessAction}
 */
export const updateMediaLibrarySuccess = (content, meta = {}) => ({
  type: actionTypes.UPDATE_MEDIA_LIBRARY_SUCCESS,
  payload: { content },
  meta,
});

/**
 * @param {string} error
 * @param {import('./types').Meta} meta

 * @returns {import('./types').UpdateMediaFailureAction}
 */
export const updateMediaLibraryFailure = (error, meta = {}) => ({
  type: actionTypes.UPDATE_MEDIA_LIBRARY_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} mediaLibraryId
 * @param {import('./types').Meta} meta
 * @return {import('./types').DeleteMediaAction}
 */
export const deleteMediaLibrary = (mediaLibraryId, meta = {}) => ({
  type: actionTypes.DELETE_MEDIA_LIBRARY,
  payload: {
    request: {
      method: 'DELETE',
      url: `${MEDIA_LIBRARY_API_URL}/${mediaLibraryId}`,
    },
  },
  mediaLibraryId,
  meta,
});

/**
 * @param {string} mediaLibraryId
 * @param {import('./types').Meta} meta
 * @returns {import('./types').DeleteMediaSuccessAction}
 */
export const deleteMediaLibrarySuccess = (mediaLibraryId, meta = {}) => ({
  type: actionTypes.DELETE_MEDIA_LIBRARY_SUCCESS,
  payload: mediaLibraryId,
  meta,
});

/**
 * @param {string} error
 * @param {import('./types').Meta} meta
 * @returns {import('./types').DeleteMediaFailureAction}
 */
export const deleteMediaLibraryFailure = (error, meta = {}) => ({
  type: actionTypes.DELETE_MEDIA_LIBRARY_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @return {import('./types').OpenMediaDrawerAction}
 */
export const openMediaDrawer = () => ({
  type: actionTypes.OPEN_MEDIA_LIBRARY_DRAWER,
});

/**
 * @return {import('./types').CloseMediaDrawerAction}
 */
export const closeMediaDrawer = () => ({
  type: actionTypes.CLOSE_MEDIA_LIBRARY_DRAWER,
});

/**
 * @return {import('./types').OpenUploadModalAction}
 */
export const openUploadModal = () => ({
  type: actionTypes.OPEN_UPLOAD_MODAL,
});

/**
 * @return {import('./types').CloseUploadModalAction}
 */
export const closeUploadModal = () => ({
  type: actionTypes.CLOSE_UPLOAD_MODAL,
});

/**
 *
 * @param  {import('./types').UploadModalMedia[]} items
 * @param {import("./types").FetchMediaLibraryPayload} params
 * @param {import('./types').Meta} meta
 * @returns {import('./types').UploadItemsAction}
 */
export const uploadItems = (items, params, meta = {}) => ({
  type: actionTypes.UPLOAD_MEDIA_LIBRARY_ITEMS,
  payload: {
    items,
    params,
  },
  meta,
});
/**
 * @param  {import('./types').UploadModalMedia[]} items
 * @param {import('./types').Meta} meta
 * @returns {import('./types').UploadItemsSuccessAction}
 */
export const uploadItemSuccess = (items, meta = {}) => ({
  type: actionTypes.UPLOAD_MEDIA_LIBRARY_ITEMS_SUCCESS,
  payload: items,
  meta,
});

/**
 * @param  {import('./types').UploadModalMedia[]} uploadedMedias
 * @param {import('./types').Meta} meta
 * @returns {import('./types').UploadItemsFailureAction}
 */
export const uploadItemFailure = (uploadedMedias, meta = {}) => ({
  type: actionTypes.UPLOAD_MEDIA_LIBRARY_ITEMS_FAILURE,
  payload: uploadedMedias,
  error: true,
  meta,
});

/**
 * @param {import('./types').AddItemPayload} data
 * @param {import('./types').Meta} meta
 * @returns {import('./types').AddItemAction}
 */
export const addItem = ({ data, meta = {} }) => ({
  type: actionTypes.ADD_ITEM_TO_MEDIA_LIBRARY,
  payload: {
    request: {
      method: 'POST',
      url: MEDIA_LIBRARY_API_URL,
      data,
    },
  },
  meta,
});

/**
 * @param {import('./types').Media} item
 * @param {import('./types').Meta} meta
 * @returns {import('./types').AddItemSuccessAction}
 */
export const addItemSuccess = (item, meta = {}) => ({
  type: actionTypes.ADD_ITEM_FROM_MEDIA_LIBRARY_SUCCESS,
  payload: item,
  meta,
});

/**
 * @param {string} error
 * @param {import('./types').Meta} meta
 * @returns {import('./types').AddItemFailureAction}
 */
export const addItemFailure = (error, meta = {}) => ({
  type: actionTypes.ADD_ITEM_FROM_MEDIA_LIBRARY_FAILURE,
  payload: error,
  error: true,
  meta,
});
